import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  doc,
  collection,
  runTransaction,
  serverTimestamp,
  addDoc,
  updateDoc,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { withRateLimit } from '../../../utils/firebaseMiddleware';
import { AuditLogger } from '../../../utils/auditLogger';
import { validateBill } from '../../../utils/validationUtils';
import { getLoyaltyConfig, findBillEarnTxs, findFifoEarnTxs } from '../../../utils/loyaltyService';
import {
  applyBillSaveToLoyaltyTx,
  reverseBillPoints,
  shouldCheckExpiry,
  checkAndExpirePoints,
} from '../../../utils/loyaltyTransactionService';
import { writeCommissionInTx, cancelCommissionByBillId } from '../../commissions';
import {
  getCurrentDate,
  getDateRange,
  fetchSupportingData,
  fetchBillsData,
  fetchCustomerActivePackages,
  softDeleteBill,
  getTrackingInfoByBillId,
  saveTrackingInfo,
  updateTrackingInfo,
} from '../services/billService';
import useBillsStore from '../store/billsStore';

export function useBills() {
  const { user } = useAuth();
  const { t } = useTranslation('home');

  // Server state — Zustand'da değil
  const [allBills, setAllBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);

  // ── Store ───────────────────────────────────────────────────
  const setToday = useBillsStore(s => s.setToday);
  const setAvailablePackages = useBillsStore(s => s.setAvailablePackages);

  // ── Initial data load ───────────────────────────────────────
  const loadAllData = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      setToday(getCurrentDate());
      const { customers: c, employees: e, services: s, packages: p } =
        await fetchSupportingData(user.businessId);
      setCustomers(c);
      setEmployees(e);
      setServices(s);
      setAvailablePackages(p);

      const bills = await fetchBillsData({
        businessId: user.businessId,
        role: user.role,
        customers: c,
        employees: e,
        t,
      });
      setAllBills(bills);
    } catch (err) {
      console.error('Veri yükleme hatası:', err);
    }
  }, [user?.businessId, user?.role, t, setToday, setAvailablePackages]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // ── Bill refresh (partial — sadece bills listesi) ─────────
  const refreshBills = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      const bills = await fetchBillsData({
        businessId: user.businessId,
        role: user.role,
        customers,
        employees,
        t,
      });
      setAllBills(bills);
    } catch (err) {
      console.error('Bill yenileme hatası:', err);
    }
  }, [user?.businessId, user?.role, customers, employees, t]);

  // ── Client-side search ─────────────────────────────────────
  const searchTerm = useBillsStore(s => s.searchTerm);

  const filteredBills = useMemo(() => {
    if (!searchTerm.trim()) return allBills;
    const q = searchTerm.toLowerCase();
    return allBills.filter((bill) => {
      const custFull = (bill.customerName || '').toLowerCase();
      const custRev = custFull.split(' ').reverse().join(' ');
      const empFull = (bill.employeeName || '').toLowerCase();
      const empRev = empFull.split(' ').reverse().join(' ');
      const svc = (bill.serviceNames || '').toLowerCase();
      const price = String(bill.totalPrice ?? '');
      return (
        custFull.includes(q) || custRev.includes(q) ||
        empFull.includes(q) || empRev.includes(q) ||
        svc.includes(q) || price.includes(q)
      );
    });
  }, [allBills, searchTerm]);

  const groupBillsByDate = useCallback((bills) =>
    bills.reduce((groups, bill) => {
      const date = bill.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(bill);
      return groups;
    }, {}),
  []);

  // ── handleOpenModal ────────────────────────────────────────
  const handleOpenModal = useCallback(async (bill = null) => {
    const store = useBillsStore.getState();

    if (!bill) {
      store.openBillModal(null);
      return;
    }

    // Edit modu — önce tracking info'yu çek, sonra store'u set et
    store.openBillModal(bill); // modalIsOpen: true, isEditing: true, selectedBillId

    const selectedCustomer = customers.find(c => c.id === bill.customerId);
    const selectedEmployee = employees.find(e => e.id === bill.employeeId);

    const expandedServices = bill.services.reduce((acc, service) => {
      const count = Math.max(1, Math.floor(Number(service.sessions) || 1));
      return [
        ...acc,
        ...Array.from({ length: count }, () => ({
          id: service.id,
          name: service.name,
          price: service.price,
        })),
      ];
    }, []);

    let initialForm = {
      date: bill.date,
      customerId: bill.customerId,
      employeeId: bill.employeeId,
      price: bill.price,
      discount: bill.discount || 0,
      paymentType: bill.paymentType || 'Nakit',
      status: bill.status,
      note: bill.note || '',
      selectedCustomer,
      selectedEmployee,
      trackingInfo: {},
      loyaltyPointsUsed: Number(bill.loyaltyPointsUsed) || 0,
      loyaltyDiscount: Number(bill.loyaltyDiscount) || 0,
      packageDiscount: Number(bill.packageDiscount) || 0,
    };

    try {
      const tracking = await getTrackingInfoByBillId(bill.id, user.businessId);
      if (tracking?.dependencies) {
        if (Array.isArray(tracking.dependencies)) {
          const map = {};
          tracking.dependencies.forEach(dep => {
            const sid = dep.serviceId || 'default';
            if (!map[sid]) map[sid] = { dependencies: {} };
            map[sid].dependencies[dep.id] = {
              name: dep.name,
              contentValue: dep.contentValue,
              description: dep.description || '',
            };
          });
          initialForm.trackingInfo = map;
        } else {
          initialForm.trackingInfo = tracking.dependencies;
        }
      }
    } catch {
      // best-effort; izin yoksa tracking bilgisi boş açılır
    }

    const savedPointsUsed = Number(bill.loyaltyPointsUsed) || 0;
    store.setEditingBillData({
      billForm: initialForm,
      applyDiscount: bill.discount > 0,
      discountAmount: bill.discount || 0,
      selectedServices: expandedServices,
      useLoyaltyPoints: savedPointsUsed > 0,
      pointsToUse: savedPointsUsed,
      originalBillCustomerId: bill.customerId || null,
    });
  }, [customers, employees]);

  // ── handleDeleteBill ───────────────────────────────────────
  const handleDeleteBill = useCallback(async (billId) => {
    try {
      await softDeleteBill(billId);
      await AuditLogger.log(user.uid, 'delete', 'bill', billId, { status: 'deleted' });

      // Loyalty puanlarını reverse et (best-effort)
      try {
        const reverseResult = await reverseBillPoints(billId, user.uid, user.businessId);
        if (reverseResult.txsCreated > 0) {
          await AuditLogger.log(user.uid, 'reverse', 'loyalty', billId, {
            billId,
            reversedPoints: reverseResult.reversedPoints,
            txsCreated: reverseResult.txsCreated,
            newTier: reverseResult.newTier,
          });
        }
      } catch (loyaltyErr) {
        // best-effort; izin yoksa sessizce geç
      }

      // Prim kaydını iptal et (best-effort)
      try {
        await cancelCommissionByBillId(billId, user.businessId);
      } catch {
        // best-effort; izin yoksa sessizce geç
      }

      toast.success(t('toasts.billDeleted'));
      await refreshBills();
    } catch (err) {
      console.error('Silme hatası:', err);
      toast.error(t('toasts.deleteError'));
    }
  }, [user, t, refreshBills]);

  // ── handleSaveBillData ─────────────────────────────────────
  const handleSaveBillData = useCallback(async () => {
    const {
      billForm,
      selectedServices,
      applyDiscount,
      discountAmount,
      isEditing,
      selectedBillId,
      originalBillCustomerId,
      useLoyaltyPoints,
      pointsToUse,
      servicePackageUsage,
      setIsSaving,
      closeBillModal,
      setCustomerActivePackages,
      setServicePackageUsage,
    } = useBillsStore.getState();

    const { isValid, errors } = validateBill(billForm);
    if (!isValid) {
      Object.values(errors).forEach(err => toast.error(err));
      return;
    }

    if (selectedServices.length === 0) {
      toast.error(t('toasts.noServiceSelected'));
      return;
    }

    setIsSaving(true);
    try {
      // Grouping
      const groupedServices = selectedServices.reduce((acc, svc) => {
        const existing = acc.find(s => s.id === svc.id);
        if (existing) {
          existing.sessions++;
        } else {
          acc.push({ id: svc.id, name: svc.name, price: svc.price, sessions: 1 });
        }
        return acc;
      }, []);

      const totalPrice = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
      const calculatedDiscount = applyDiscount ? discountAmount : 0;

      const packageItems = selectedServices.filter(s => s._isPackage);
      const regularServices = selectedServices.filter(s => !s._isPackage);

      const hasCustomer = Boolean(billForm.customerId);
      const customerChanged =
        isEditing && originalBillCustomerId && originalBillCustomerId !== billForm.customerId;

      // Loyalty config
      const loyaltyConfig = hasCustomer ? await getLoyaltyConfig(user.businessId) : null;

      // Lazy expiry check
      if (hasCustomer) {
        const selectedCust = customers.find(c => c.id === billForm.customerId);
        if (selectedCust && shouldCheckExpiry(selectedCust.loyalty)) {
          try {
            const expiryResult = await checkAndExpirePoints(billForm.customerId, user.businessId);
            if (expiryResult.expiredPoints > 0) {
              try {
                await AuditLogger.log(user.uid, 'expire', 'loyalty', billForm.customerId, {
                  expiredPoints: expiryResult.expiredPoints,
                  txsCreated: expiryResult.txsCreated,
                  newTier: expiryResult.newTier,
                  trigger: 'bill-save',
                });
              } catch (auditErr) {
                console.warn('Expire audit log başarısız:', auditErr);
              }
              setCustomers(prev => prev.map(c =>
                c.id === billForm.customerId
                  ? {
                      ...c,
                      loyalty: {
                        ...(c.loyalty || {}),
                        currentPoints: Math.max(0, (Number(c.loyalty?.currentPoints) || 0) - expiryResult.expiredPoints),
                        tier: expiryResult.newTier || c.loyalty?.tier || 'bronze',
                        lastExpiryCheckAt: new Date(),
                      },
                    }
                  : c
              ));
              toast.warning(t('toasts.loyaltyExpiry', { count: expiryResult.expiredPoints }));
              return;
            }
          } catch (err) {
            console.warn('Bill save öncesi expiry check başarısız (devam ediliyor):', err);
          }
        }
      }

      // Loyalty redeem hesabı
      let billLoyaltyPointsUsed = 0;
      let billLoyaltyDiscount = 0;
      let pointsToRedeemNow = 0;

      if (isEditing) {
        billLoyaltyPointsUsed = Number(billForm.loyaltyPointsUsed) || 0;
        billLoyaltyDiscount   = Number(billForm.loyaltyDiscount) || 0;
      } else if (hasCustomer && useLoyaltyPoints && pointsToUse > 0 && loyaltyConfig) {
        const selectedCust = customers.find(c => c.id === billForm.customerId);
        const custCurrentPoints = Number(selectedCust?.loyalty?.currentPoints) || 0;
        const requestedPoints = Math.max(0, Math.floor(Number(pointsToUse) || 0));
        const minRedeem = Number(loyaltyConfig.minRedeem) || 0;
        const redeemRate = Number(loyaltyConfig.redeemRate) || 1;
        const maxUsable = Math.min(
          custCurrentPoints,
          Math.floor((totalPrice - calculatedDiscount) / redeemRate)
        );

        if (requestedPoints < minRedeem) {
          toast.error(t('toasts.loyaltyMinRedeem', { min: minRedeem }));
          return;
        }
        if (requestedPoints > maxUsable) {
          toast.error(t('toasts.loyaltyMaxUsable', { max: maxUsable }));
          return;
        }

        pointsToRedeemNow     = requestedPoints;
        billLoyaltyPointsUsed = requestedPoints;
        billLoyaltyDiscount   = requestedPoints * redeemRate;
      }

      // Paket indirimi
      const packageDiscountAmount = isEditing
        ? (Number(billForm.packageDiscount) || 0)
        : regularServices.reduce(
            (sum, s) => servicePackageUsage[s.id] ? sum + Number(s.price) : sum, 0
          );

      const finalTotalPrice = Math.max(
        0, totalPrice - calculatedDiscount - billLoyaltyDiscount - packageDiscountAmount
      );

      const packageUsages = Object.entries(servicePackageUsage).map(([serviceId, cpId]) => {
        const sessionsUsed = regularServices.filter(s => s.id === serviceId).length;
        const svcName = regularServices.find(s => s.id === serviceId)?.name || '';
        return { customerPackageId: cpId, serviceId, serviceName: svcName, sessions: sessionsUsed };
      });

      const billData = {
        customerId: billForm.customerId,
        employeeId: billForm.employeeId,
        paymentType: billForm.paymentType,
        date: billForm.date,
        price: totalPrice,
        discount: calculatedDiscount,
        loyaltyPointsUsed: billLoyaltyPointsUsed,
        loyaltyDiscount: billLoyaltyDiscount,
        packageDiscount: packageDiscountAmount,
        packageUsages: packageUsages.length > 0 ? packageUsages : null,
        totalPrice: finalTotalPrice,
        services: groupedServices,
        status: 'active',
        userId: user.uid,
        businessId: user.businessId,
        note: billForm.note || '',
      };

      // Edit'te müşteri değiştiyse eski puanları reverse et
      if (customerChanged) {
        try {
          const oldRev = await reverseBillPoints(selectedBillId, user.uid, user.businessId);
          if (oldRev.txsCreated > 0) {
            await AuditLogger.log(user.uid, 'reverse', 'loyalty', selectedBillId, {
              billId: selectedBillId,
              reason: 'customer-change',
              oldCustomerId: originalBillCustomerId,
              reversedPoints: oldRev.reversedPoints,
              txsCreated: oldRev.txsCreated,
            });
          }
        } catch (err) {
          console.error('Müşteri değişimi: eski puanları reverse ederken hata:', err);
          toast.warning(t('toasts.customerChangedReverseFailed'));
        }
      }

      // Pre-query: edit aynı müşteri → earn docs; yeni bill + redeem → FIFO earn docs
      const existingEarnDocs = (isEditing && hasCustomer && !customerChanged)
        ? await findBillEarnTxs(selectedBillId, user.businessId)
        : [];
      const redeemEarnDocs = (!isEditing && hasCustomer && pointsToRedeemNow > 0)
        ? await findFifoEarnTxs(billForm.customerId, pointsToRedeemNow, user.businessId)
        : [];

      const billRef = isEditing
        ? doc(db, 'bills', selectedBillId)
        : doc(collection(db, 'bills'));
      const savedBillId = billRef.id;
      const customerRef = hasCustomer ? doc(db, 'users', billForm.customerId) : null;

      let loyaltyResult = null;

      await withRateLimit(user.uid, async () => {
        // === Ana transaction: Bill + Loyalty + Commission atomik ===
        await runTransaction(db, async (tx) => {
          // READ PHASE
          const customerSnap = customerRef ? await tx.get(customerRef) : null;
          const existingEarnSnaps = existingEarnDocs.length > 0
            ? await Promise.all(existingEarnDocs.map(e => tx.get(e.ref)))
            : [];
          const redeemEarnSnaps = redeemEarnDocs.length > 0
            ? await Promise.all(redeemEarnDocs.map(e => tx.get(e.ref)))
            : [];

          // WRITE PHASE — Bill
          if (isEditing) {
            tx.update(billRef, { ...billData, updatedAt: serverTimestamp() });
          } else {
            tx.set(billRef, { ...billData, createdAt: serverTimestamp() });
          }

          // WRITE PHASE — Loyalty
          if (customerRef && customerSnap && loyaltyConfig) {
            loyaltyResult = applyBillSaveToLoyaltyTx(tx, {
              customerRef,
              customerSnap,
              existingEarnRefs: existingEarnDocs.map(e => e.ref),
              existingEarnSnaps,
              redeemEarnRefs: redeemEarnDocs.map(e => e.ref),
              redeemEarnSnaps,
              pointsToRedeem: pointsToRedeemNow,
              billId: savedBillId,
              totalPrice: finalTotalPrice,
              employeeId: user.uid,
              config: loyaltyConfig,
              businessId: user.businessId,
            });

            if (loyaltyResult?.pointsEarned > 0) {
              tx.update(billRef, { loyaltyPointsEarned: loyaltyResult.pointsEarned });
            }
          }

          // WRITE PHASE — Commission (sadece yeni bill)
          if (!isEditing) {
            const emp = employees.find(e => e.id === billData.employeeId);
            const commConfig = emp?.commissionConfig;
            if (commConfig) {
              writeCommissionInTx(tx, {
                bill: { id: savedBillId, date: billData.date, employeeId: billData.employeeId, services: groupedServices },
                commissionConfig: commConfig,
                businessId: user.businessId,
              });
            }
          }
        });

        // === Paket seans deduction (tx dışında) ===
        for (const usage of packageUsages) {
          try {
            const cpRef = doc(db, 'customer_packages', usage.customerPackageId);
            await runTransaction(db, async (tx) => {
              const cpSnap = await tx.get(cpRef);
              if (!cpSnap.exists()) return;
              const cp = cpSnap.data();
              const rem = { ...(cp.remainingSessions || {}) };
              rem[usage.serviceId] = Math.max(0, (rem[usage.serviceId] || 0) - usage.sessions);
              const totalLeft = Object.values(rem).reduce((s, v) => s + v, 0);
              tx.update(cpRef, {
                remainingSessions: rem,
                usageHistory: [
                  ...(cp.usageHistory || []),
                  {
                    date: billForm.date,
                    billId: savedBillId,
                    serviceId: usage.serviceId,
                    serviceName: usage.serviceName,
                    sessions: usage.sessions,
                  },
                ],
                status: totalLeft <= 0 ? 'completed' : 'active',
                updatedAt: serverTimestamp(),
              });
            });
          } catch (err) {
            console.warn('Paket seans düşme hatası:', err.message);
          }
        }

        // === Ürün stok deduction (best-effort, sadece yeni bill) ===
        if (!isEditing) {
          const productSales = groupedServices.filter(svc => {
            const details = services.find(s => s.id === svc.id);
            return details?.type === 'Ürün' && details?.trackStock;
          });
          for (const sale of productSales) {
            try {
              await updateDoc(doc(db, 'services', sale.id), {
                stockQty: increment(-sale.sessions),
              });
              await addDoc(collection(db, 'stock_movements'), {
                businessId: user.businessId,
                serviceId: sale.id,
                serviceName: sale.name,
                type: 'out',
                qty: sale.sessions,
                note: `Adisyon #${savedBillId}`,
                createdBy: user.uid,
                createdAt: serverTimestamp(),
              });
            } catch (err) {
              console.warn('Stok düşme hatası:', err.message);
            }
          }
        }

        // === Paket satışı — customer_packages oluştur (tx dışında) ===
        if (!isEditing && packageItems.length > 0 && billForm.customerId) {
          for (const pkgItem of packageItems) {
            try {
              let expiresAt = null;
              if ((pkgItem._validityDays || 0) > 0) {
                const exp = new Date();
                exp.setDate(exp.getDate() + Number(pkgItem._validityDays));
                expiresAt = Timestamp.fromDate(exp);
              }
              const totalSessions = {};
              const remainingSessions = {};
              (pkgItem._packageServices || []).forEach(svc => {
                totalSessions[svc.serviceId] = svc.sessions;
                remainingSessions[svc.serviceId] = svc.sessions;
              });
              await addDoc(collection(db, 'customer_packages'), {
                businessId: user.businessId,
                customerId: billForm.customerId,
                packageId: pkgItem.id,
                packageName: pkgItem.name,
                packageServices: pkgItem._packageServices || [],
                purchasedDate: billForm.date,
                expiresAt,
                purchaseBillId: savedBillId,
                totalSessions,
                remainingSessions,
                usageHistory: [],
                status: 'active',
                note: '',
                createdAt: serverTimestamp(),
              });
            } catch (err) {
              console.warn('customer_packages oluşturma hatası:', err.message);
              toast.warning(`Paket kaydedilemedi: ${pkgItem.name}`);
            }
          }
        }

        // === Tracking Info (idempotent yan efekt) ===
        if (billForm.trackingInfo && Object.keys(billForm.trackingInfo).length > 0) {
          const trackingPayload = {
            billId: savedBillId,
            userId: user.uid,
            businessId: user.businessId,
            dependencies: billForm.trackingInfo,
          };
          try {
            const existing = await getTrackingInfoByBillId(savedBillId, user.businessId);
            if (existing) {
              await updateTrackingInfo(existing.id, trackingPayload);
            } else {
              await saveTrackingInfo(trackingPayload);
            }
          } catch (err) {
            console.error('Tracking info kayıt hatası:', err);
          }
        }
      });

      // === Audit log (loyalty) ===
      const hasLoyaltyActivity = loyaltyResult && (
        loyaltyResult.pointsEarned > 0 ||
        loyaltyResult.pointsRedeemed > 0 ||
        loyaltyResult.reversedCount > 0
      );
      if (hasLoyaltyActivity) {
        try {
          await AuditLogger.log(
            user.uid,
            isEditing ? 'update' : 'create',
            'loyalty',
            loyaltyResult.earnTxId || loyaltyResult.redeemTxId || savedBillId,
            {
              billId: savedBillId,
              customerId: billForm.customerId,
              pointsEarned: loyaltyResult.pointsEarned,
              pointsRedeemed: loyaltyResult.pointsRedeemed,
              loyaltyDiscount: loyaltyResult.loyaltyDiscount,
              reversedCount: loyaltyResult.reversedCount,
              reversedPoints: loyaltyResult.reversedPoints,
              oldTier: loyaltyResult.oldTier,
              newTier: loyaltyResult.newTier,
            }
          );
        } catch (e) {
          console.error('Loyalty audit log başarısız:', e);
        }
      }

      await refreshBills();
      closeBillModal();

      const baseMsg = isEditing ? t('toasts.billUpdated') : t('toasts.billAdded');
      const messages = [baseMsg];
      if (loyaltyResult) {
        if (loyaltyResult.pointsRedeemed > 0)
          messages.push(t('toasts.loyaltyPointsRedeemed', {
            count: loyaltyResult.pointsRedeemed, discount: loyaltyResult.loyaltyDiscount
          }));
        if (loyaltyResult.pointsEarned > 0)
          messages.push(t('toasts.loyaltyPointsEarned', { count: loyaltyResult.pointsEarned }));
        if (loyaltyResult.oldTier !== loyaltyResult.newTier)
          messages.push(t('toasts.tierChanged', { from: loyaltyResult.oldTier, to: loyaltyResult.newTier }));
      }
      toast.success(messages[0]);
      messages.slice(1).forEach(msg => toast.info(msg, { autoClose: 5000 }));
    } catch (err) {
      console.error('İşlem hatası:', err);
      toast.error(t('toasts.saveError', { error: err.message }));
    } finally {
      useBillsStore.getState().setIsSaving(false);
    }
  }, [user, customers, employees, services, t, refreshBills]);

  // ── handleCustomerPackageLoad (BillModal customerId değişimi) ──
  const handleCustomerChange = useCallback(async (customerId) => {
    const store = useBillsStore.getState();
    if (customerId) {
      store.setServicePackageUsage({});
      try {
        const pkgs = await fetchCustomerActivePackages(user.businessId, customerId);
        store.setCustomerActivePackages(pkgs);
      } catch {
        store.setCustomerActivePackages([]);
      }
    } else {
      store.setCustomerActivePackages([]);
      store.setServicePackageUsage({});
    }
  }, [user?.businessId]);

  // ── createBillFromAppointment ──────────────────────────────
  // Adisyon modalı başarıyla açıldıysa true, akış başlayamadıysa false döner
  // (çağıran taraf randevu rollback'i için bu sonucu kullanır).
  const createBillFromAppointment = useCallback((appointmentData) => {
    if (!appointmentData) return false;
    const { customerId, employeeId, services: apptServices, notes, paymentMethod } = appointmentData;

    if (customers.length === 0 || employees.length === 0) {
      toast.error(t('toasts.loadError'));
      return false;
    }

    const selectedCustomer = customers.find(c => c.id === customerId);
    const selectedEmployee = employees.find(e => e.id === employeeId);

    if (!selectedCustomer || !selectedEmployee) {
      toast.error(t('toasts.appointmentCustomerNotFound'));
      return false;
    }

    let billServices = [];
    if (Array.isArray(apptServices)) {
      billServices = apptServices.reduce((acc, svc) => {
        const details = services.find(s => s.id === svc.id);
        if (details) {
          return [
            ...acc,
            ...Array(svc.sessions || 1).fill({
              id: svc.id,
              name: svc.name || details.name,
              price: svc.price || details.price,
            }),
          ];
        }
        return acc;
      }, []);
    }

    if (billServices.length === 0) {
      toast.warning(t('toasts.appointmentNoServices'));
    }

    // Tüm modal state'ini TEK atomik set ile hazırla. selectedServices modal
    // açılırken hazır olmalı; aksi halde (eski setTimeout kurgusu) modal, global
    // store'da hâlâ duran BİR ÖNCEKİ adisyonun servisleriyle açılıyor ve BillModal
    // o servislerin trackingInfo dependency alanlarını gösteriyordu.
    useBillsStore.getState().setEditingBillData({
      isEditing: false,
      selectedBillId: null,
      originalBillCustomerId: null,
      billForm: {
        date: getCurrentDate(),
        customerId,
        employeeId,
        price: '',
        discount: '',
        paymentType: paymentMethod || 'Nakit',
        status: 'active',
        note: notes || '',
        selectedCustomer,
        selectedEmployee,
        trackingInfo: {},
        loyaltyPointsUsed: 0,
        loyaltyDiscount: 0,
        packageDiscount: 0,
      },
      selectedServices: billServices,
      applyDiscount: false,
      discountAmount: 0,
      useLoyaltyPoints: false,
      pointsToUse: 0,
      servicePackageUsage: {},
      customerActivePackages: [],
      modalIsOpen: true,
    });

    return true;
  }, [customers, employees, services, t]);

  // ── Composed services (normal + packages) for BillModal ───
  const availablePackages = useBillsStore(s => s.availablePackages);
  const composedServices = useMemo(() => [
    ...services,
    ...availablePackages.map(pkg => ({
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      _isPackage: true,
      _packageServices: pkg.services || [],
      _validityDays: pkg.validityDays || 0,
    })),
  ], [services, availablePackages]);

  return {
    // Data
    allBills,
    filteredBills,
    customers,
    employees,
    services: composedServices,
    // Helpers
    groupBillsByDate,
    // Handlers
    loadAllData,
    handleOpenModal,
    handleDeleteBill,
    handleSaveBillData,
    handleCustomerChange,
    createBillFromAppointment,
  };
}
